/**
 * StepTooltip — card retro posicionado próximo ao spotlight.
 *
 * Conteúdo:
 * - Eyebrow Plus Jakarta Sans verde "// PASSO N/M"
 * - Título Plus Jakarta Sans coral
 * - Descrição Plus Jakarta Sans com efeito typewriter
 * - Barra de progresso (track + fill) N/M
 * - Botões: Voltar, Pular Tour, Próximo
 *
 * Atalhos de teclado: ←/→ navega, Esc pula, Enter completa typewriter→próximo
 * Posicionamento: calcula se cabe na tela, ajusta quando necessário.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import type { OnboardingStep } from '../utils/onboardingSteps';
import { useElementRect } from '../hooks/useElementRect';
import { useTypewriter } from '../hooks/useTypewriter';
import '../styles/onboarding.css';

/** Padding entre o elemento alvo e o tooltip */
const GAP = 16;
/** Padding mínimo das bordas da tela */
const EDGE_PAD = 12;

interface StepTooltipProps {
  step: OnboardingStep;
  /** Índice global do step (0-based) */
  index: number;
  /** Total de steps */
  total: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  t: (key: string) => string;
}

/** Calcula o progresso (0–100%) para a barra visual */
function buildProgressPercent(current: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
}

export function StepTooltip({
  step,
  index,
  total,
  onNext,
  onBack,
  onSkip,
  t,
}: StepTooltipProps) {
  const rect = useElementRect(step.selectorTarget ?? null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const title = t(step.titleKey);
  const desc  = t(step.descKey);
  const ctaLabel = step.ctaKey ? t(step.ctaKey) : t('onboarding.next');

  // Typewriter para a descrição
  const { displayed, isComplete, skip: skipTypewriter } = useTypewriter(desc, {
    speedMs: 25,
    startDelayMs: 80,
  });

  // Calcula posição do tooltip relativo ao rect + posição desejada
  const tooltipStyle = useMemo((): React.CSSProperties => {
    if (!rect) {
      // Step sem elemento alvo ou rect ainda não resolvido → centro da tela
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const pos = step.position ?? 'bottom';
    const tooltipW = 340;
    const tooltipH = 220; // estimativa

    let top: number;
    let left: number;

    switch (pos) {
      case 'bottom':
        top  = rect.top + rect.height + GAP;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      case 'top':
        top  = rect.top - tooltipH - GAP;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      case 'right':
        top  = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.left + rect.width + GAP;
        break;
      case 'left':
        top  = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.left - tooltipW - GAP;
        break;
      default:
        top  = rect.top + rect.height + GAP;
        left = rect.left + rect.width / 2 - tooltipW / 2;
    }

    // Ajusta para não sair da tela
    left = Math.max(EDGE_PAD, Math.min(left, window.innerWidth - tooltipW - EDGE_PAD));
    top  = Math.max(EDGE_PAD, Math.min(top, window.innerHeight - tooltipH - EDGE_PAD));

    return { top, left };
  }, [rect, step.position]);

  // Atalhos de teclado
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          if (!isComplete) {
            skipTypewriter();
          } else {
            onNext();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onBack();
          break;
        case 'Escape':
          e.preventDefault();
          onSkip();
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isComplete, skipTypewriter, onNext, onBack, onSkip]);

  // Foco automático no botão "Próximo" quando tooltip monta
  useEffect(() => {
    const btn = tooltipRef.current?.querySelector<HTMLButtonElement>('.onb-btn--next');
    btn?.focus();
  }, [step.id]);

  const progressPercent = buildProgressPercent(index, total - 1);

  // Número do step intermediário (exclui welcome=0 e final=último)
  const stepNum = index; // já começa em 1 na prática (welcome é tratado separado)

  return (
    <div
      ref={tooltipRef}
      className="onb-tooltip"
      role="dialog"
      aria-modal="false"
      aria-label={title}
      style={tooltipStyle}
    >
      {/* Mascote pera Pixar — companheira do tour (border violet + glow violet) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '-32px',
          left: '-16px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'var(--ink-soft)',
          border: '2px solid var(--violet)',
          boxShadow: '0 0 12px rgba(124, 124, 248, 0.55), 0 0 28px rgba(124, 124, 248, 0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src="/icons/icon-128.png"
          alt=""
          width={56}
          height={56}
          style={{
            display: 'block',
            objectFit: 'contain',
            filter: 'drop-shadow(0 0 4px rgba(124, 124, 248, 0.45))',
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {/* Eyebrow */}
      <div className="onb-tooltip__eyebrow">
        // PASSO {stepNum}/{total - 2}
      </div>

      {/* Título */}
      <div className="onb-tooltip__title">{title}</div>

      {/* Barra de progresso */}
      <div className="onb-progress">
        <span className="onb-progress__track" aria-hidden="true">
          <span className="onb-progress__fill" style={{ width: `${progressPercent}%` }} />
        </span>
        <span>{stepNum}/{total - 2}</span>
      </div>

      {/* Descrição com typewriter */}
      <div className="onb-tooltip__desc">
        {displayed}
        {!isComplete && <span className="onb-cursor" aria-hidden="true" />}
      </div>

      {/* Botões de navegação */}
      <div className="onb-btn-group">
        {/* Voltar — oculto no primeiro step intermediário */}
        {index > 1 && (
          <button
            type="button"
            className="onb-btn onb-btn--back"
            onClick={onBack}
            aria-label={t('onboarding.back')}
          >
            ◀ {t('onboarding.back')}
          </button>
        )}

        {/* Próximo / CTA */}
        <button
          type="button"
          className="onb-btn onb-btn--next"
          onClick={() => {
            if (!isComplete) {
              skipTypewriter();
            } else {
              onNext();
            }
          }}
          aria-label={ctaLabel}
        >
          {isComplete ? ctaLabel : 'PULAR TEXTO'}
        </button>

        {/* Pular tour */}
        <button
          type="button"
          className="onb-btn onb-btn--skip"
          onClick={onSkip}
          aria-label={t('onboarding.skip')}
        >
          {t('onboarding.skip')}
        </button>
      </div>
    </div>
  );
}
